import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  ViewChild,
  computed,
  signal,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSort, MatSortable } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { LicenseService } from '../../../shared/license-list/services/license.service';
import { LocalSettingsService } from '../../../../lib/services/local-settings.service';
import {
  LicenseManagerOverrideItem,
  LicenseManagerOverrideResponse,
} from '../../../shared/license-list/models/license-override-list.model';
import { GroupOverrideDialogComponent } from './group-override-dialog/group-override-dialog.component';
import { NihiiOverrideDialogComponent } from './nihii-override-dialog/nihii-override-dialog.component';
import {
  firstValueFrom,
  from,
  map,
  Observable,
  of,
  tap,
  catchError,
} from 'rxjs';
import { NihiiHelper } from 'src/app/shared/helpers/nihii.helper';

@Component({
  selector: 'ms-licenses',
  templateUrl: './licenses.component.html',
  styleUrls: ['./licenses.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class LicensesComponent implements OnInit {
  public readonly groupColumns: string[] = [
    'groupId',
    'type',
    'reason',
    'actions',
  ];
  public readonly nihiiColumns: string[] = [
    'nihii',
    'type',
    'reason',
    'actions',
  ];
  public readonly searchTerm = signal<string>('');
  public readonly normalizedSearchTerm = computed<string>(() =>
    this.searchTerm().trim().toLowerCase(),
  );
  public readonly groupOverrides = signal<LicenseManagerOverrideItem[]>([]);
  public readonly nihiiOverrides = signal<LicenseManagerOverrideItem[]>([]);
  public readonly managerError = signal<string | null>(null);
  public readonly groupCount = computed<number>(() =>
    this.countGroupOverrides(),
  );
  public readonly nihiiCount = computed<number>(() =>
    this.countNihiiOverrides(),
  );
  public groupOverridesDataSource: MatTableDataSource<LicenseManagerOverrideItem> =
    new MatTableDataSource<LicenseManagerOverrideItem>();
  public nihiiOverridesDataSource: MatTableDataSource<LicenseManagerOverrideItem> =
    new MatTableDataSource<LicenseManagerOverrideItem>();

  @ViewChild('groupSort', { static: false })
  public groupSort: MatSort | undefined;

  @ViewChild('nihiiSort', { static: false })
  public nihiiSort: MatSort | undefined;

  constructor(
    private licenseService: LicenseService,
    private localSettingsService: LocalSettingsService,
    private dialog: MatDialog,
  ) {}

  public openNihiiOverrideDialog(): void {
    const dialogRef = this.dialog.open(NihiiOverrideDialogComponent, {
      width: '560px',
    });

    dialogRef.afterClosed().subscribe((created: boolean | undefined): void => {
      if (created) {
        void this.refreshOverrides();
      }
    });
  }

  public openGroupOverrideDialog(): void {
    const dialogRef = this.dialog.open(GroupOverrideDialogComponent, {
      width: '560px',
    });

    dialogRef.afterClosed().subscribe((created: boolean | undefined): void => {
      if (created) {
        void this.refreshOverrides();
      }
    });
  }

  public async ngOnInit(): Promise<void> {
    const token: string | null = this.localSettingsService.oauthIdToken;
    if (!token) {
      return;
    }

    await this.refreshOverrides();
  }

  public async refreshOverrides(): Promise<void> {
    const token: string | null = this.localSettingsService.oauthIdToken;
    if (!token) {
      return;
    }

    await firstValueFrom(this.loadOverrides$(token));
  }

  private loadOverrides$(token: string): Observable<void> {
    return from(this.licenseService.loadManagerOverrideList(token)).pipe(
      tap((response: LicenseManagerOverrideResponse): void => {
        this.managerError.set(null);
        const groupOverrides: LicenseManagerOverrideItem[] =
          this.getGroupOverrides(response);
        const nihiiOverrides: LicenseManagerOverrideItem[] =
          this.getNihiiOverrides(response);
        this.groupOverrides.set(groupOverrides);
        this.nihiiOverrides.set(nihiiOverrides);
        this.groupOverridesDataSource = this.createDataSource(groupOverrides);
        this.nihiiOverridesDataSource = this.createDataSource(nihiiOverrides);
        this.applySearchFilter();
        this.attachSorting();
      }),
      catchError((error: unknown): Observable<void> => {
        this.managerError.set(this.getErrorMessage(error));
        return of(undefined);
      }),
      map((): void => undefined),
    );
  }

  public async deleteGroupOverride(groupId: string): Promise<void> {
    const token: string | null = this.localSettingsService.oauthIdToken;
    if (!token) {
      return;
    }

    try {
      await this.licenseService.deleteGroupOverride(token, groupId);
      this.groupOverrides.update((items: LicenseManagerOverrideItem[]) =>
        items.filter(
          (item: LicenseManagerOverrideItem): boolean =>
            item.group_id !== groupId,
        ),
      );
      this.groupOverridesDataSource.data = this.groupOverrides();
      this.applySearchFilter();
    } catch (error: unknown) {
      this.managerError.set(this.getErrorMessage(error));
    }
  }

  public async deleteNihiiOverride(nihii: string): Promise<void> {
    const token: string | null = this.localSettingsService.oauthIdToken;
    if (!token) {
      return;
    }

    try {
      await this.licenseService.deleteNihiiOverride(token, nihii);
      this.nihiiOverrides.update((items: LicenseManagerOverrideItem[]) =>
        items.filter(
          (item: LicenseManagerOverrideItem): boolean => item.nihii !== nihii,
        ),
      );
      this.nihiiOverridesDataSource.data = this.nihiiOverrides();
      this.applySearchFilter();
    } catch (error: unknown) {
      this.managerError.set(this.getErrorMessage(error));
    }
  }

  private getGroupOverrides(
    response: LicenseManagerOverrideResponse,
  ): LicenseManagerOverrideItem[] {
    return response.overridden_entity.filter(
      (item: LicenseManagerOverrideItem): boolean =>
        !!item.group_id && item.type !== 'IN_ORDER',
    );
  }

  private getNihiiOverrides(
    response: LicenseManagerOverrideResponse,
  ): LicenseManagerOverrideItem[] {
    return response.overridden_entity.filter(
      (item: LicenseManagerOverrideItem): boolean =>
        !!item.nihii && item.type !== 'IN_ORDER',
    );
  }

  private getErrorMessage(error: unknown): string {
    if (error instanceof Error) {
      return error.message;
    }

    return 'Impossible de charger la liste de blocage du serveur';
  }

  private attachSorting(): void {
    if (this.groupSort) {
      this.groupOverridesDataSource.sort = this.groupSort;
      this.groupSort.sort({ id: 'groupId', start: 'asc' } as MatSortable);
    }

    if (this.nihiiSort) {
      this.nihiiOverridesDataSource.sort = this.nihiiSort;
      this.nihiiSort.sort({ id: 'nihii', start: 'asc' } as MatSortable);
    }
  }

  private createDataSource(
    items: LicenseManagerOverrideItem[],
  ): MatTableDataSource<LicenseManagerOverrideItem> {
    const dataSource: MatTableDataSource<LicenseManagerOverrideItem> =
      new MatTableDataSource(items);
    dataSource.sortingDataAccessor = (
      item: LicenseManagerOverrideItem,
      property: string,
    ): string => {
      switch (property) {
        case 'groupId':
          return item.group_id ?? '';
        case 'nihii':
          return item.nihii ?? '';
        case 'type':
          return item.type;
        case 'reason':
          return item.reason;
        default:
          return '';
      }
    };

    return dataSource;
  }

  public updateSearchTerm(term: string): void {
    this.searchTerm.set(term);
    this.applySearchFilter();
  }

  private applySearchFilter(): void {
    const filterValue: string = this.normalizedSearchTerm();
    this.groupOverridesDataSource.filterPredicate = (
      item: LicenseManagerOverrideItem,
      filter: string,
    ): boolean => {
      return this.matchesGroupFilter(item, filter);
    };
    this.nihiiOverridesDataSource.filterPredicate = (
      item: LicenseManagerOverrideItem,
      filter: string,
    ): boolean => {
      return this.matchesNihiiFilter(item, filter);
    };

    this.groupOverridesDataSource.filter = filterValue;
    this.nihiiOverridesDataSource.filter = filterValue;
  }

  public getGroupTabLabel(): string {
    return `Base de données (${this.groupCount()})`;
  }

  public getNihiiTabLabel(): string {
    return `INAMI (${this.nihiiCount()})`;
  }

  private countGroupOverrides(): number {
    const filter: string = this.normalizedSearchTerm();
    return this.groupOverrides().filter(
      (item: LicenseManagerOverrideItem): boolean =>
        this.matchesGroupFilter(item, filter),
    ).length;
  }

  private countNihiiOverrides(): number {
    const filter: string = this.normalizedSearchTerm();
    return this.nihiiOverrides().filter(
      (item: LicenseManagerOverrideItem): boolean =>
        this.matchesNihiiFilter(item, filter),
    ).length;
  }

  private matchesGroupFilter(
    item: LicenseManagerOverrideItem,
    filter: string,
  ): boolean {
    if (!filter) {
      return true;
    }

    const normalizedFilter = filter.toLowerCase();

    return (item.group_id ?? '').toLowerCase().includes(normalizedFilter);
  }

  private matchesNihiiFilter(
    item: LicenseManagerOverrideItem,
    filter: string,
  ): boolean {
    if (!filter) {
      return true;
    }

    if (this.containsLetters(filter)) {
      return false;
    }

    const normalizedNihii: string = NihiiHelper.normalizeToNihii8(
      item.nihii ?? '',
    );
    const normalizedFilter: string = NihiiHelper.normalizeToNihii8(filter);

    if (!normalizedFilter) {
      return true;
    }

    if (normalizedFilter.length < 8) {
      return normalizedNihii.includes(normalizedFilter);
    }

    return normalizedFilter.startsWith(normalizedNihii);
  }

  private containsLetters(value: string): boolean {
    return /[a-z]/i.test(value);
  }
}
